import React, { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useLazyGetAiAgentResultQuery } from '../../../store/ai/aiAgentResult'
import {
  resetEmailResponse,
  setEmailResponse,
  setEmailResponseAiAgentId,
  setEmailResponseCitationLoading,
  setEmailResponseCitations,
  setEmailResponseLoading,
} from '../../../store/emailHub/emailResponse'
import { AIAgentType } from '../../../models/AIAgent'
import { EmailResponseResult } from '../../../models/EmailResponse'
import { selectCurrentUserEmailIntegration } from '../../../store/emailHub/emailUsers'
import { useLazyGetEmailMetadataQuery } from '../../../store/integrations/emailMetadata'
import { updateEmailMetadata } from '../../../store/emailHub/emailMetadata'
import { parseMetadata } from '../../../apps/professional-app/fa/MeetingSummary/utils'
import { isEmailResponseNoResult } from '../EmailResponse/utils'
import { setMessages } from '../../../store/streamingChat/streamingChat'

export interface EmailResponseInitializationProps {
  emailId: string
}

export const EmailResponseInitialization: React.FC<EmailResponseInitializationProps> = ({ emailId }) => {
  useInitializeEmailResponse(emailId)
  useInitializeEmailMetadata(emailId)

  return null
}

const useInitializeEmailResponse = (emailId: string) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const currentUserId = useAppSelector(selectSafeAuthenticatedUser).id
  const emailIntegration = useAppSelector(selectCurrentUserEmailIntegration)
  const emailConnector = emailIntegration?.serviceId ?? null

  const [triggerGetAiAgentResult] = useLazyGetAiAgentResultQuery()

  useEffect(() => {
    const fetchEmailResponseResult = async () => {
      if (!emailConnector) {
        return
      }
      dispatch(setEmailResponseLoading(true))
      try {
        const { data: aiAgentResult } = await triggerGetAiAgentResult({
          accountId,
          type: AIAgentType.EMAIL_RESPONSE_V2,
          resultKey: `${currentUserId}|${emailConnector}|${emailId}`,
        })
        if (!aiAgentResult) {
          return
        }
        const { originalAiAgentId, result, interaction, metadata } = aiAgentResult
        const { aiAgentId, interactions = [] } = interaction ?? {}
        const { citations, startedGenerating, emailSignatureProcessed } = parseMetadata(metadata)
        if (!isEmailResponseNoResult(result as EmailResponseResult) && !emailSignatureProcessed) {
          return
        }
        dispatch(setEmailResponseAiAgentId(aiAgentId ?? originalAiAgentId))
        dispatch(setEmailResponse(result as EmailResponseResult))
        dispatch(setMessages(interactions.map((i) => i.result)))
        if (citations) {
          dispatch(setEmailResponseCitations(citations))
        } else if (startedGenerating) {
          dispatch(setEmailResponseCitationLoading(true))
        }
      } finally {
        dispatch(setEmailResponseLoading(false))
      }
    }
    fetchEmailResponseResult()
    return () => {
      dispatch(resetEmailResponse())
    }
  }, [dispatch, accountId, currentUserId, emailConnector, emailId, triggerGetAiAgentResult])
}

const useInitializeEmailMetadata = (emailId: string) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const currentUserId = useAppSelector(selectSafeAuthenticatedUser).id
  const emailIntegration = useAppSelector(selectCurrentUserEmailIntegration)
  const emailConnector = emailIntegration?.serviceId ?? null

  const [triggerGetEmailMetadata] = useLazyGetEmailMetadataQuery()

  useEffect(() => {
    const fetchEmailMetadata = async () => {
      if (!emailConnector) {
        return
      }
      const { data: emailMetadata } = await triggerGetEmailMetadata({
        accountId,
        emailConnector,
        externalId: emailId,
      })
      if (!emailMetadata) {
        return
      }
      dispatch(updateEmailMetadata(emailMetadata))
    }
    fetchEmailMetadata()
  }, [dispatch, accountId, currentUserId, emailConnector, emailId, triggerGetEmailMetadata])
}
